import React, { useEffect, useState } from 'react';
import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  notification,
  Row,
} from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { IDataModal } from '@/interfaces/dataModal';
import { ILocationCheckingRes } from '@/interfaces/location';
import { getFullLocationAddress } from '@/utils/customerUtils';
import {
  APIProvider,
  Map,
  AdvancedMarker,
  MapCameraChangedEvent,
  Pin,
  MapMouseEvent,
} from '@vis.gl/react-google-maps';
import { getLocationCount, updateLocationChecking } from '@/adapters/report';
import { AxiosResponse } from 'axios';

interface IProps {
  dataModal?: IDataModal<Partial<ILocationCheckingRes>>;
  handleOk: () => void;
  handleCancel: () => void;
}

const ShippingAddressModal = ({
  dataModal,
  handleOk,
  handleCancel,
}: IProps) => {
  const [form] = Form.useForm<Partial<ILocationCheckingRes>>();
  const oldLat = Number(dataModal?.item?.lat || 0);
  const oldLng = Number(dataModal?.item?.lng || 0);
  const [point, setPoint] = useState<{
    lat: number;
    lng: number;
  }>({
    lat: Number(dataModal?.item?.driverLat || 0),
    lng: Number(dataModal?.item?.driverLng || 0),
  });
  const [center, setCenter] = useState({
    lat: Number(dataModal?.item?.lat || dataModal?.item?.driverLat || 0),
    lng: Number(dataModal?.item?.lng || dataModal?.item?.driverLng || 0),
  });

  const onMapClick = (e: MapMouseEvent) => {
    setPoint({
      lat: e.detail?.latLng?.lat || 0,
      lng: e?.detail?.latLng?.lng || 0,
    });
    form.setFieldsValue({
      driverLat: e.detail?.latLng?.lat || 0,
      driverLng: e?.detail?.latLng?.lng || 0,
    });
  };

  const { refetch } = useQuery<AxiosResponse<number>, Error>({
    queryKey: ['count-location-checking'],
    queryFn: async () => await getLocationCount({ isLocationUpdate: true }),
  });

  const mutations = useMutation({
    mutationFn: async (data: Partial<ILocationCheckingRes>) =>
      updateLocationChecking(data),
    mutationKey: ['update-location-res'],
    onSuccess: data => {
      if (data?.status != 200) {
        throw new Error();
      } else {
        notification.success({
          message: 'อัพเดทที่อยู่สำเร็จ',
        });
        refetch();
        handleOk();
      }
    },
    onError: error => {
      console.log(error);
    },
  });

  const handleUpdate = async () => {
    form
      .validateFields()
      .then(async (values: Partial<ILocationCheckingRes>) => {
        await mutations.mutateAsync({
          ...dataModal?.item,
          ...values,
        });
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  const onReset = () => {
    form.resetFields();
  };

  useEffect(() => {
    if (dataModal?.id) {
      form.setFieldsValue({
        ...dataModal?.item,
      });
    }
  }, [dataModal?.id, dataModal?.item, form]);

  return (
    <Modal
      title={'ที่อยู่ขนส่ง'}
      open={dataModal?.open}
      onOk={() => handleUpdate()}
      onCancel={handleCancel}
      okText="บันทึก"
      width={1000}
      cancelText="ยกเลิก"
      footer={[
        <Button key="back" onClick={handleCancel}>
          ยกเลิก
        </Button>,
        <Button
          key="submit"
          type="primary"
          form="productGroup"
          loading={mutations.isPending}
          onClick={handleUpdate}
        >
          บันทึก
        </Button>,
      ]}
      afterClose={onReset}
    >
      <div className="flex w-full">
        <Form
          form={form}
          name="productGroup"
          onFinish={handleUpdate}
          layout="vertical"
          className="w-full"
        >
          <Row gutter={24} className="flex items-end">
            <Col span={24} md={12}>
              <Row gutter={12}>
                <Col span={24} md={24}>
                  <Form.Item
                    label="ชื่อย่อ"
                    name="shortName"
                    className="w-full"
                  >
                    <Input size="large" readOnly />
                  </Form.Item>
                </Col>
                <Col span={24} md={24}>
                  <Form.Item label="ที่อยู่" className="w-full">
                    <Input
                      size="large"
                      value={getFullLocationAddress(
                        dataModal?.item as ILocationCheckingRes,
                      )}
                      readOnly
                    />
                  </Form.Item>
                </Col>
                <Col span={24} md={24}>
                  <Form.Item<ILocationCheckingRes>
                    label="Map link"
                    name="maplink"
                  >
                    <Input size="large" placeholder="Map link" />
                  </Form.Item>
                </Col>
                <Col span={12} md={12}>
                  <Form.Item<ILocationCheckingRes>
                    label="Original Lat"
                    name="lat"
                  >
                    <Input size="large" placeholder="Original Lat" />
                  </Form.Item>
                </Col>
                <Col span={12} md={12}>
                  <Form.Item<ILocationCheckingRes>
                    label="Original Lng"
                    name="lng"
                  >
                    <Input size="large" placeholder="Original Lng" />
                  </Form.Item>
                </Col>
                <Col span={12} md={12}>
                  <Form.Item<ILocationCheckingRes>
                    label="Driver Lat"
                    name="driverLat"
                    rules={[{ required: true }]}
                  >
                    <InputNumber
                      size="large"
                      placeholder="Driver Lat"
                      className="w-full"
                      min={0.01}
                    />
                  </Form.Item>
                </Col>
                <Col span={12} md={12}>
                  <Form.Item<ILocationCheckingRes>
                    label="Driver Lng"
                    name="driverLng"
                    rules={[{ required: true }]}
                  >
                    <InputNumber
                      size="large"
                      className="w-full"
                      placeholder="Driver Lng"
                      min={0.01}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
            <Col span={24} md={12}>
              <div
                style={{
                  height: 370,
                  width: '100%',
                  marginBottom: 10,
                }}
              >
                <APIProvider
                  apiKey={'AIzaSyC3LUICKk1b104OLYosRFxRgOYysgSrD_o'}
                  onLoad={() => console.log('Maps API has loaded.')}
                >
                  <Map
                    defaultZoom={12}
                    defaultCenter={center}
                    onClick={onMapClick}
                    onCameraChanged={(ev: MapCameraChangedEvent) => {
                      setCenter(ev.detail.center);
                    }}
                    mapId="x12"
                    center={center}
                  >
                    {oldLat != 0 && (
                      <AdvancedMarker position={{ lat: oldLat, lng: oldLng }}>
                        <Pin
                          background={'#FF3F33'}
                          glyphColor={'#000'}
                          borderColor={'#000'}
                        />
                      </AdvancedMarker>
                    )}
                    {point?.lat != 0 && (
                      <AdvancedMarker position={point} clickable={true}>
                        <Pin
                          background={'#FBBC04'}
                          glyphColor={'#000'}
                          borderColor={'#000'}
                        />
                      </AdvancedMarker>
                    )}
                  </Map>
                </APIProvider>
              </div>
            </Col>
          </Row>
        </Form>
      </div>
    </Modal>
  );
};

export default ShippingAddressModal;
